import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { flushSync } from "react-dom";

import { ChartData, ChartFormatEnum } from "@/__generated__/graphql";
import { Utils } from "@/utils";

/** How long a chart keeps animating after the numbers behind it change. */
const ANIMATES_FOR_MS = 400;

/**
 * Whether a chart should animate right now.
 *
 * A chart animates when what it shows changes - the bars grow into their new
 * heights - and never when its box changes. A resize is not new data: there is
 * nothing to follow from one state to the next, and animating it leaves the
 * old axis labels on screen while the new ones arrive, which is how a drag
 * used to smear them across each other.
 */
export function useAnimatesOnChange(data: unknown): boolean {
  const previous = useRef(data);
  const changedAt = useRef(0);
  const [, redraw] = useState(0);

  // Read while rendering, not after it: told a render late, the chart has
  // already drawn the new numbers by the time it hears it may animate them.
  if (previous.current !== data) {
    previous.current = data;
    changedAt.current = performance.now();
  }
  const animates = performance.now() - changedAt.current < ANIMATES_FOR_MS;

  useEffect(() => {
    if (!animates) return;
    // Once the animation is over, draw once more without it, so the resize
    // that follows is not caught up in it.
    const done = setTimeout(
      () => redraw((count) => count + 1),
      ANIMATES_FOR_MS,
    );
    return () => clearTimeout(done);
  }, [animates]);

  return animates;
}

/**
 * The size a chart is drawn at, from one observer.
 *
 * A chart used to be measured twice over - once by nivo's responsive wrapper,
 * once here for working out which axis labels fit - so a drag cost two renders
 * a frame, and the labels were fitted to the width of the frame before: the
 * axis drew as many labels as the old width held, in the new width, which is
 * how they came to overlap. Measured once and drawn at that size, the labels
 * can only be the ones that fit what is on screen.
 */
export function usePlotSize() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Whole pixels: a drag reports fractional widths, and a chart redrawn for
    // a third of a pixel is a frame spent on nothing.
    const measure = (width: number, height: number) =>
      setSize((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height },
      );

    measure(Math.round(element.clientWidth), Math.round(element.clientHeight));
    const observer = new ResizeObserver(([entry]) => {
      // Redrawn on React's own schedule, the chart misses the frame its box
      // changed in: for that frame it is the old chart at the new size, which
      // is an axis carrying more labels than it now has room for. A resize
      // observer runs after layout and before paint, so committing here puts
      // the redrawn chart in the same frame as the box that moved.
      flushSync(() => {
        measure(
          Math.round(entry.contentRect.width),
          Math.round(entry.contentRect.height),
        );
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, width: size.width, height: size.height };
}

export function toBarData(chart: ChartData) {
  return {
    data: chart.points.map((p) => ({
      label: p.label,
      ...Object.fromEntries(p.values.map((v) => [v.key, v.value])),
    })),
    keys: chart.series,
    indexBy: "label" as const,
  };
}

export function toLineData(chart: ChartData) {
  return chart.series.map((s) => ({
    id: s,
    data: chart.points.map((p) => ({
      x: p.label,
      y: p.values.find((v) => v.key === s)?.value ?? 0,
    })),
  }));
}

/**
 * A pie's slices: each as large as its part is, money out as much as money in -
 * spending is summed below nothing, and a slice cannot be - with what it was,
 * its sign and all, to say.
 */
export function toPieData(chart: ChartData) {
  return chart.points.map((p) => {
    const said = p.values[0]?.value ?? 0;
    return { id: p.label, value: Math.abs(said), said };
  });
}

export function formatLabel(value: string, format: ChartFormatEnum): string {
  switch (format) {
    case ChartFormatEnum.Date:
      return Utils.fDate(new Date(value));
    case ChartFormatEnum.Currency:
      return Utils.fCurrency(value);
    default:
      return value;
  }
}

/** Axis tick text size, in px — a number so the fitting maths below is honest. */
export const AXIS_FONT_SIZE = 11;

/** How far a value-axis tick's text sits off the axis itself. */
export const AXIS_TICK_OFFSET = 8;

/** The room an axis's own name takes beside the numbers on it. */
export const AXIS_NAME_ROOM = 22;

/**
 * The subset of ``labels`` that fits ``availableWidth`` without colliding.
 *
 * Nivo ignores a numeric ``tickValues`` on band and point scales, so an axis
 * left to itself renders *every* category — a year of ``Feb 01, 26'`` smeared
 * over itself in a dashboard card. Handing it an explicit array is the only
 * way to thin them.
 */
export function fittingAxisLabels(
  labels: string[],
  format: ChartFormatEnum,
  availableWidth: number,
): string[] {
  if (labels.length === 0) return labels;
  const widest = labels.reduce(
    (max, label) => Math.max(max, formatLabel(label, format).length),
    0,
  );
  // ~0.62em of advance width per character in the UI font, plus a gutter.
  const perLabel = widest * AXIS_FONT_SIZE * 0.62 + 12;
  const fits = Math.floor(availableWidth / perLabel);
  if (fits >= labels.length) return labels;
  const stride = Math.ceil(labels.length / Math.max(fits, 1));
  return labels.filter((_, index) => index % stride === 0);
}

/**
 * The room the value axis needs, measured off the labels it drew.
 *
 * Worked out instead of measured, this was always wrong: a canvas cannot
 * resolve the custom property the theme names its font with, so it answers in
 * its own default and reports a string a fifth narrower than the one on
 * screen - and a guess padded enough to be safe leaves an empty column down
 * the side of every chart. The labels are right there once drawn, so the
 * margin grows to whatever they turn out to need, before the browser paints.
 */
export function useFittedValueAxis(
  ref: RefObject<HTMLElement | null>,
  resetKey: unknown,
): number {
  const [gutter, setGutter] = useState(0);

  // New numbers can be shorter than the old ones; measure those afresh rather
  // than keeping a gutter the chart has grown out of.
  useLayoutEffect(() => setGutter(0), [resetKey]);

  useLayoutEffect(() => {
    const labels = ref.current?.querySelectorAll('[data-axis="value"]');
    if (!labels?.length) return;
    const widest = Math.max(
      ...[...labels].map((label) => label.getBoundingClientRect().width),
    );
    if (!widest) return;
    const needed = Math.ceil(widest) + AXIS_TICK_OFFSET + 2;
    // Only ever wider: growing the margin narrows the plot, which does not
    // change how wide a number is, so this settles on the next render.
    setGutter((current) => (needed > current ? needed : current));
  });

  return gutter;
}

export function formatValue(
  value: number | string,
  format: ChartFormatEnum,
): string {
  switch (format) {
    case ChartFormatEnum.Currency:
      return Utils.fCurrency(value);
    case ChartFormatEnum.Percent:
      return `${value}%`;
    case ChartFormatEnum.Integer:
      return Math.round(Number(value)).toString();
    default:
      return String(value);
  }
}
